import {Trans} from '@lingui/macro';
import {isUndefined} from 'lodash';
import React, {useEffect, useRef, useState} from 'react';
import {Key} from 'ts-key-enum';

import {useInitialEffect} from '../../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {useDebounce} from '../../../../../../srcCommon/helpers/hookHelpers';
import {SearchToken} from '../../../../../../srcCommon/inboxes/search/searchHelpers';
import {ModifiersEnum} from '../../../../../../srcCommon/inboxes/search/searchTypes';
import {
  buildCancelTokenSource,
  CancelError,
  CancelTokenSource,
} from '../../../../../helpers/asyncCancelHelpers';
import {useAppSelector} from '../../../../../helpers/reduxHelpers';
import {AccountModel} from '../../../../../models/api/accountModel';
import {ApiSearchAccountsV2} from '../../../../../models/api/searchAccountsV2Model';
import {fetchApiAsync} from '../../../../../services/http/http';
import {buildSearchAccountsV2Url} from '../../../../../store/data/accounts/accountsActions';
import {getCompany} from '../../../../../store/data/company/companySelectors';
import {FuzzyHighlight} from '../../../cells/header/search/highlight/fuzzyHighlight';
import {
  InboxesSearchSuggestionItemProps,
  InboxesSearchSuggestionsBaseProps,
  InboxesSuggestionsDropdownItem,
  InboxesSuggestionsDropdownSection,
  InboxesSuggestionsDropdownSectionTitle,
  InboxesSuggestionsDropdownSectionWrapper,
  SEARCH_DROPDOWN_DEBOUNCE_WAIT_MS,
} from './inboxesSearchSuggestionsDropdownCommons';
import {renderAccountResource} from './inboxesSearchSuggestionsDropdownSmartMix';

/*
 * Constants.
 */

const accountsSearchLimit = 20;

/*
 * Props.
 */

interface InboxesSearchSuggestionsDropdownAccountAsyncProps extends InboxesSearchSuggestionsBaseProps {}

/*
 * Component.
 */

// @TODO: Refactor `InboxesSearchSuggestionsDropdownResourceAsync` to avoid duplication.
export const InboxesSearchSuggestionsDropdownAccountAsync = (
  props: InboxesSearchSuggestionsDropdownAccountAsyncProps,
) => {
  const {activeToken} = props;

  const company = useAppSelector(getCompany);

  const [accountItems, setAccountItems] = useState<
    ReadonlyArray<AccountModel & InboxesSearchSuggestionItemProps>
  >([]);

  const cancelTokenSourceRef = useRef<CancelTokenSource>();
  const maybeCancelRequest = () => {
    const currentCancelTokenSource = cancelTokenSourceRef.current;
    return currentCancelTokenSource && currentCancelTokenSource.cancel();
  };

  useInitialEffect(() => {
    fetchAccountsAsync(activeToken);

    return () => maybeCancelRequest();
  });

  const fetchAccountsAsync = async (searchToken: SearchToken) => {
    // Cancel any existing request.
    maybeCancelRequest();

    const cancelTokenSource = buildCancelTokenSource();
    cancelTokenSourceRef.current = cancelTokenSource;

    if (
      isUndefined(searchToken.value) ||
      isUndefined(searchToken.modifier) ||
      searchToken.modifier !== ModifiersEnum.ACCOUNT
    ) {
      return setAccountItems([]);
    }

    const {token} = cancelTokenSource;
    const encodedQuery = encodeURIComponent(searchToken.value);
    const path = buildSearchAccountsV2Url(company.url, {
      query: encodedQuery,
      limit: accountsSearchLimit,
    });

    try {
      const response = await fetchApiAsync(ApiSearchAccountsV2, path, {cancelToken: token});
      const {accounts} = response;

      return setAccountItems(
        accounts.map((account) => ({
          ...account,
          value: computeAccountValue(account),
          primaryLabel: <FuzzyHighlight query={searchToken.value} text={String(account.name)} />,
          secondaryLabel:
            account.domains.length > 0 ? (
              <FuzzyHighlight query={searchToken.value} text={computeDomainsString(account.domains)} />
            ) : undefined,
        })),
      );
    } catch (e) {
      if (!(e instanceof CancelError)) {
        throw e;
      }

      return null;
    }
  };

  const fetchAccountsAsyncDebounced = useDebounce(fetchAccountsAsync, SEARCH_DROPDOWN_DEBOUNCE_WAIT_MS);

  useEffect(() => {
    fetchAccountsAsyncDebounced(activeToken);
  }, [activeToken, fetchAccountsAsyncDebounced]);

  if (accountItems.length === 0) {
    return null;
  }

  const onSelect = (item: InboxesSearchSuggestionItemProps, key?: Key) =>
    props.onSuggestionSelect(item.value, key, {
      insertWhiteSpace: true,
      isModifierSuggestion: true,
      modifierSuggestionType: activeToken.modifier,
    });

  return (
    <InboxesSuggestionsDropdownSectionWrapper>
      <InboxesSuggestionsDropdownSection>
        <InboxesSuggestionsDropdownSectionTitle>
          <Trans id="inboxes-search-accounts-suggestions">Accounts</Trans>
        </InboxesSuggestionsDropdownSectionTitle>
        {accountItems.map((accountItem) => (
          <InboxesSuggestionsDropdownItem
            onSelect={(_e, key) => onSelect(accountItem, key)}
            key={accountItem.name}
          >
            {renderAccountResource(accountItem)}
          </InboxesSuggestionsDropdownItem>
        ))}
      </InboxesSuggestionsDropdownSection>
    </InboxesSuggestionsDropdownSectionWrapper>
  );
};

/*
 * Helpers.
 */

function computeDomainsString(domains: ReadonlyArray<string>) {
  if (domains.length === 0) {
    return '';
  }

  return `• ${domains.join(', ')}`;
}

function computeAccountValue(account: AccountModel) {
  return `${ModifiersEnum.ACCOUNT}:${account.alias}`;
}
